/**
 * What the office's PMS calls a policy's carrier, next to ours.
 *
 * A policy imported from the PMS keeps the PMS's own carrier label
 * (`sikka_carrier_name_raw`). When it names something other than the carrier we
 * filed the policy under — the PMS says "GMS Insurance Inc", we say "Telus" — the
 * FTC needs to see both: they phone the insurer on the card, and a claims network
 * like Telus will just send them back to it.
 *
 * Spelling variants of the same name are NOT a difference ("Manulife" vs
 * "Manulife Financial", "Green Shield" vs "GreenShield Insurance"), or every
 * card would carry noise.
 */

// Words that only decorate a carrier's name. Deliberately NOT "canada" or
// "life": those carry identity ("Canada Life", "Sun Life").
const DECORATION = new Set([
  "the", "of", "and", "inc", "ltd", "limited", "corp", "corporation", "co", "company",
  "insurance", "assurance", "financial", "group", "services", "service",
  "benefits", "benefit", "plan", "plans", "administrators", "administrator", "admin",
]);

function squash(name) {
  return String(name || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(function (t) { return t && !DECORATION.has(t); })
    .join("");
}

/** True when the PMS label names something other than our carrier. */
export function pmsLabelDiffers(ourName, pmsName) {
  const pms = squash(pmsName);
  const ours = squash(ourName);
  if (!pms || !ours) return false;
  return pms.indexOf(ours) === -1 && ours.indexOf(pms) === -1;
}

/** The PMS label worth showing for a policy, or null when it adds nothing. */
export function pmsCarrierLabel(ourName, pmsName) {
  const raw = String(pmsName || "").trim();
  return raw && pmsLabelDiffers(ourName, raw) ? raw : null;
}

/**
 * The note shown after a carrier corrected by hand also became the office's
 * answer for that PMS carrier (the backend's `mapping_repointed`).
 */
export function describeRepoint(repointed) {
  if (!repointed || !repointed.to) return null;
  const pms = repointed.pms_name ? "“" + repointed.pms_name + "”" : "this carrier";
  return "From now on, when your PMS says " + pms + ", we'll use " + repointed.to + ".";
}
